/**
 * The Disposal page's Registrations tab: disposal companies that signed themselves up and
 * verified their email wait here for Paelode to approve (link to an existing disposal company,
 * or create one) or reject. Until now nothing in web-admin could do either.
 */
import * as React from 'react';
import { useUrlParam } from '../../lib/url-state';
import { toast } from 'sonner';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import {
  useApproveDisposalRegistration,
  useDisposalCompanies,
  useDisposalRegistrations,
  useRejectDisposalRegistration,
  type DisposalRegistrationRow,
  type DisposalRegistrationStatus,
} from './hooks';
import {
  LICENSE_MATCH_LABELS,
  REGISTRATION_STATUS_LABELS,
  REJECT_REASON_MIN,
  VERIFICATION_NOTE_MIN,
  approveBody,
  disposalErrorMessage,
  needsVerificationNote,
  type ApproveDraft,
} from './disposal-labels';

function fmtDate(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const STATUS_TABS: ReadonlyArray<{ id: DisposalRegistrationStatus; label: string }> = [
  { id: 'EMAIL_VERIFIED', label: 'Awaiting review' },
  { id: 'PENDING_EMAIL_VERIFY', label: 'Email not verified' },
  { id: 'APPROVED', label: 'Approved' },
  { id: 'REJECTED', label: 'Rejected' },
];

export function DisposalRegistrationsTab(): React.JSX.Element {
  // The status filter lives in the URL (lib/url-state.ts); the cursor stack stays in memory.
  const [statusParam, setStatus] = useUrlParam('regStatus', 'EMAIL_VERIFIED', []);
  const status = statusParam as DisposalRegistrationStatus;
  // Cursor paging: the stack of cursors that led to this page (null = the first page).
  const [cursors, setCursors] = React.useState<ReadonlyArray<string | null>>([null]);
  const cursor = cursors[cursors.length - 1] ?? null;
  const [approving, setApproving] = React.useState<DisposalRegistrationRow | null>(null);
  const [rejecting, setRejecting] = React.useState<DisposalRegistrationRow | null>(null);
  const { data, isLoading, isError, refetch } = useDisposalRegistrations(status, cursor);
  const rows = data?.registrations ?? [];

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {STATUS_TABS.map((t) => (
          <Button
            key={t.id}
            size="sm"
            variant={status === t.id ? 'default' : 'outline'}
            onClick={() => {
              setStatus(t.id);
              setCursors([null]);
            }}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {isLoading && <p style={subText}>Loading registrations…</p>}
      {isError && (
        <p style={{ color: 'var(--color-error)', fontSize: '14px' }}>
          Failed to load registrations.{' '}
          <button
            type="button"
            onClick={() => void refetch()}
            style={{
              textDecoration: 'underline',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              color: 'inherit',
            }}
          >
            Retry
          </button>
        </p>
      )}
      {!isLoading && !isError && rows.length === 0 && (
        <p style={subText}>No registrations: {REGISTRATION_STATUS_LABELS[status].toLowerCase()}.</p>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>License</TableHead>
              <TableHead>Facility</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`disposal-registration-${r.id}`}>
                <TableCell style={{ fontSize: '13px' }}>
                  <div style={{ fontWeight: 600 }}>{r.companyName}</div>
                  <div style={subText}>{r.email}</div>
                </TableCell>
                <TableCell style={{ fontSize: '13px' }}>
                  {r.contactName}
                  <div style={subText}>{r.phone}</div>
                </TableCell>
                <TableCell style={{ fontSize: '13px' }}>
                  {r.licenseNumber ?? '—'}
                  {r.state !== null ? ` (${r.state})` : ''}
                  <div>
                    <Pill
                      label={LICENSE_MATCH_LABELS[r.licenseMatchStatus]}
                      tone={needsVerificationNote(r.licenseMatchStatus) ? 'warning' : 'positive'}
                    />
                  </div>
                </TableCell>
                <TableCell style={{ fontSize: '13px' }}>
                  {r.matchedFacilityDisplayName ?? r.facilityName ?? '—'}
                </TableCell>
                <TableCell style={{ fontSize: '13px' }}>{fmtDate(r.createdAt)}</TableCell>
                <TableCell>
                  {r.status === 'EMAIL_VERIFIED' && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button size="sm" onClick={() => setApproving(r)}>
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setRejecting(r)}>
                        Reject
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {(cursors.length > 1 || data?.hasMore === true) && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <Button
            size="sm"
            variant="outline"
            disabled={cursors.length <= 1}
            onClick={() => setCursors(cursors.slice(0, -1))}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={data?.hasMore !== true || data.nextCursor === null}
            onClick={() => {
              if (data?.nextCursor != null) setCursors([...cursors, data.nextCursor]);
            }}
          >
            Next
          </Button>
        </div>
      )}

      {approving !== null && (
        <ApproveDisposalDialog registration={approving} onClose={() => setApproving(null)} />
      )}
      {rejecting !== null && (
        <RejectDisposalDialog registration={rejecting} onClose={() => setRejecting(null)} />
      )}
    </div>
  );
}

export function ApproveDisposalDialog({
  registration,
  onClose,
}: {
  registration: DisposalRegistrationRow;
  onClose: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = React.useState<ApproveDraft>({
    mode: 'new',
    disposalCompanyId: '',
    name: registration.companyName.toUpperCase(),
    slug: '',
    legalName: '',
    note: '',
  });
  // The companies a registration can join (the list endpoint's max page).
  const companies = useDisposalCompanies(null, 0, 100);
  const approve = useApproveDisposalRegistration(registration.id, () => {
    toast.success(`${registration.companyName} approved.`);
    onClose();
  });
  const body = approveBody(draft, registration.licenseMatchStatus);
  const noteRequired = needsVerificationNote(registration.licenseMatchStatus);
  const set = (patch: Partial<ApproveDraft>): void => setDraft({ ...draft, ...patch });

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent style={{ maxWidth: '520px' }} data-testid="disposal-approve-dialog">
        <DialogHeader>
          <DialogTitle>Approve {registration.companyName}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (body !== null) approve.mutate(body);
          }}
        >
          <p style={{ ...subText, margin: '0 0 12px' }}>
            {registration.contactName} ({registration.email}) gets a disposal portal sign-in. A new
            company starts as payment pending: activate it on the Companies tab once it has paid.
          </p>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '13px' }}>
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input
                type="radio"
                name="approve-mode"
                checked={draft.mode === 'new'}
                onChange={() => set({ mode: 'new' })}
              />
              Create a new disposal company
            </label>
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input
                type="radio"
                name="approve-mode"
                checked={draft.mode === 'existing'}
                onChange={() => set({ mode: 'existing' })}
              />
              Add to an existing one
            </label>
          </div>

          {draft.mode === 'new' ? (
            <>
              <FormRow label="Company name" htmlFor="dispName" required>
                <input
                  id="dispName"
                  style={fieldStyle}
                  value={draft.name}
                  onChange={(e) => set({ name: e.target.value })}
                  maxLength={200}
                />
              </FormRow>
              <FormRow label="Legal name" htmlFor="dispLegal">
                <input
                  id="dispLegal"
                  style={fieldStyle}
                  value={draft.legalName}
                  onChange={(e) => set({ legalName: e.target.value })}
                  maxLength={200}
                />
              </FormRow>
              <FormRow label="Slug (optional)" htmlFor="dispSlug">
                <input
                  className="keep-case"
                  id="dispSlug"
                  style={fieldStyle}
                  value={draft.slug}
                  placeholder="made from the name when blank"
                  onChange={(e) => set({ slug: e.target.value })}
                  maxLength={63}
                />
              </FormRow>
            </>
          ) : (
            <FormRow label="Disposal company" htmlFor="dispExisting" required>
              <select
                id="dispExisting"
                style={fieldStyle}
                value={draft.disposalCompanyId}
                onChange={(e) => set({ disposalCompanyId: e.target.value })}
              >
                <option value="">Choose a company…</option>
                {(companies.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </FormRow>
          )}

          <FormRow
            label={noteRequired ? 'Verification note' : 'Verification note (optional)'}
            htmlFor="dispNote"
            required={noteRequired}
          >
            <textarea
              id="dispNote"
              style={{ ...fieldStyle, minHeight: '64px', resize: 'vertical' }}
              value={draft.note}
              onChange={(e) => set({ note: e.target.value })}
              placeholder={
                noteRequired
                  ? `The license did not match a known facility: how was it checked? (${String(VERIFICATION_NOTE_MIN)}+ characters)`
                  : ''
              }
              maxLength={1000}
            />
          </FormRow>

          {approve.error != null && (
            <p role="alert" style={{ fontSize: '13px', color: 'var(--color-error)' }}>
              {disposalErrorMessage(approve.error)}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="disposal-approve-submit"
              disabled={body === null || approve.isPending}
            >
              {approve.isPending ? 'Approving…' : 'Approve'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RejectDisposalDialog({
  registration,
  onClose,
}: {
  registration: DisposalRegistrationRow;
  onClose: () => void;
}): React.JSX.Element {
  const [reason, setReason] = React.useState('');
  const [notify, setNotify] = React.useState(true);
  const reject = useRejectDisposalRegistration(registration.id, () => {
    toast.success(`Registration for ${registration.email} rejected.`);
    onClose();
  });
  const ok = reason.trim().length >= REJECT_REASON_MIN;

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent style={{ maxWidth: '460px' }} data-testid="disposal-reject-dialog">
        <DialogHeader>
          <DialogTitle>Reject {registration.companyName}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (ok) reject.mutate({ reason: reason.trim(), notifyApplicant: notify });
          }}
        >
          <FormRow label="Reason" htmlFor="dispRejectReason" required>
            <textarea
              id="dispRejectReason"
              style={{ ...fieldStyle, minHeight: '80px', resize: 'vertical' }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={`Why this registration cannot be approved (${String(REJECT_REASON_MIN)}+ characters)`}
              maxLength={500}
            />
          </FormRow>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}>
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
            Email the applicant
          </label>
          {reject.error != null && (
            <p role="alert" style={{ fontSize: '13px', color: 'var(--color-error)' }}>
              {disposalErrorMessage(reject.error)}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="disposal-reject-submit"
              disabled={!ok || reject.isPending}
            >
              {reject.isPending ? 'Rejecting…' : 'Reject'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
